<script setup lang="ts">
import { ref, reactive, watch, onMounted, computed } from 'vue'
import {
  createAccountProductsConfig,
  getParentAccount,
  fetchAllProductsFromIndex,
} from '@/api/accountsAdmin'
import { useRouter } from 'vue-router'
import { useAccountStore } from '@/stores/accountStore'
const router = useRouter()
const accountStore = useAccountStore()
const parentAccountData = ref([])
const parentOptions = ref<any[]>([])
const parentOptionsLoading = ref(false)
const existingProducts = ref<any[]>([])
const productNameError = ref('')

interface Field {
  key: string
  type: string
  required: boolean
  arrayType?: 'string' | 'number' | 'object'
  objectProperties?: Field[]
  arrayObjectProperties?: Field[]
  defaultValue?: boolean
}

const schema = reactive({
  productName: '',
  productType: 'parent',
  parentPath: '',

  config: { type: 'object', properties: {}, required: [] as string[] },
  commonData: { dashboard: '', showToCustomers: true, productFriendlyName: '' },
})

const dashboardUrl = ref('')
const showToCustomers = ref(true)
const errorMessages = ref<string[]>([])
watch(dashboardUrl, (newUrl) => {
  validateUrl(newUrl)
  schema.commonData.dashboard = newUrl
})
watch(showToCustomers, (newVal: boolean) => {
  schema.commonData.showToCustomers = newVal
})

const validateUrl = (url: string) => {
  const regex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i
  errorMessages.value = regex.test(url) ? [] : ['Invalid URL']
}

const fields = ref<Field[]>([])
const loading = ref(false)
const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref<'success' | 'error' | 'info' | 'warning'>('success')
const showSnack = (text: string, color: 'success' | 'error' | 'info' | 'warning' = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  snackbar.value = true
}

const addField = () => {
  fields.value.push({
    key: "",
    type: "string",
    required: false,
    arrayType: "string",
    objectProperties: [],
    arrayObjectProperties: [],
    defaultValue: false,
  })
}
const addObjectProperty = (field: Field) => {
  if (!field.objectProperties) {
    field.objectProperties = []
  }
  field.objectProperties.push({ key: '', type: 'string', required: false })
}
const addArrayObjectProperty = (field: Field) => {
  if (!field.arrayObjectProperties) {
    field.arrayObjectProperties = []
  }
  field.arrayObjectProperties.push({ key: '', type: 'string', required: false })
}
const removeField = (index: number) => {
  fields.value.splice(index, 1)
}
const removeObjectProperty = (field: Field, index: number) => {
  field.objectProperties?.splice(index, 1)
}
const removeArrayObjectProperty = (field: Field, index: number) => {
  field.arrayObjectProperties?.splice(index, 1)
}
const sanitizeProductName = (name: string) => name.replace(/[ -]/g, '_').toLowerCase()

// Check if product name already exists
const checkProductNameExists = (name: string) => {
  if (!name) {
    productNameError.value = ''
    return false
  }

  const sanitizedName = sanitizeProductName(name)
  console.log('Checking product name:', name, '-> sanitized:', sanitizedName)
  console.log('Existing products count:', existingProducts.value.length)

  const exists = existingProducts.value.some((product: any) => {
    // Check against productId (simple ID) and product (bucketPath)
    const matches = product.productId === sanitizedName || product.product === sanitizedName
    if (matches) {
      console.log('Found duplicate:', product)
    }
    return matches
  })

  if (exists) {
    productNameError.value = 'A product with this name already exists'
    return true
  }

  productNameError.value = ''
  return false
}

watch(
  () => schema.productName,
  (newValue) => {
    schema.commonData.productFriendlyName = newValue
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())

    // Check if product name exists
    checkProductNameExists(newValue)
  }
)
watch(
  fields,
  (newFields) => {
    const buildProperties = (fields: Field[], parentIsRequired: boolean = true) => {
      const properties: Record<string, any> = {}
      const required: string[] = []
      fields.forEach((field) => {
        if (!field.key) return
        let prop: Record<string, any> = { type: field.type }
        if (field.type === 'object' && field.objectProperties?.length) {
          const { properties: objProps, required: objRequired } = buildProperties(
            field.objectProperties,
            field.required && parentIsRequired
          )
          prop.properties = objProps
          if (objRequired.length && field.required && parentIsRequired) {
            prop.required = objRequired
          }
        }
        if (field.type === 'array') {
          if (field.arrayType === 'object' && field.arrayObjectProperties?.length) {
            const { properties: objProps, required: objRequired } = buildProperties(
              field.arrayObjectProperties,
              parentIsRequired && field.required
            )
            prop.items = {
              type: "object",
              properties: objProps,
              ...(objRequired.length && parentIsRequired && field.required
                ? { required: objRequired }
                : {}),
            }
          } else {
            prop.items = { type: field.arrayType }
          }
        }
        if (field.required && parentIsRequired) {
          required.push(field.key)
        }
        properties[field.key] = prop
      })
      return { properties, required }
    }
    const { properties, required } = buildProperties(newFields, true)
    schema.config.properties = properties
    schema.config.required = required
  },
  { deep: true }
)

const submitSchema = async () => {
  if (schema.productName === '') {
    showSnack('Product name can not be empty', 'error')
    return
  }
  loading.value = true
  try {
    const submissionSchema = JSON.parse(JSON.stringify(schema))
    submissionSchema.productName = sanitizeProductName(submissionSchema.productName)
    await createAccountProductsConfig(submissionSchema)
    showSnack('Product created successfully', 'success')
    try {
      accountStore.setProductsCache([])
    } catch {}
    await new Promise((r) => setTimeout(r, 1200))
    router.push('/products')
    loading.value = false
  } catch (error) {
    console.error('Submission error:', error)
    showSnack('Error submitting product', 'error')
    loading.value = false
  } finally {
    loading.value = false
  }
}
const getParentAccounts = async () => {
  try {
    const res = await getParentAccount()
    parentAccountData.value = (res as any).data
  } catch (error) {
    console.error('Error Fetching Parent Account:', error)
  }
}

const loadParentOptionsFromIndex = async () => {
  try {
    parentOptionsLoading.value = true

    const response = await fetchAllProductsFromIndex()
    const allProducts = response?.products || []

    // For new products, we don't need to exclude the current product
    // Just map all products to the option format
    parentOptions.value = allProducts
      .map((p: any) => ({
        ...p,
        path: String(p?.path || p?.productId || ''),
        displayName: `${p?.productFriendlyName || p?.productId || ''} (${p?.path || p?.productId || ''})`,
      }))
      .sort((a: any, b: any) => {
        if (a.level !== b.level) return a.level - b.level
        return (a.productFriendlyName || '').localeCompare(b.productFriendlyName || '')
      })

    console.log('Parent options loaded:', parentOptions.value.length, 'products')
  } catch (e) {
    console.error('Error loading parent options:', e)
    parentOptions.value = []
  } finally {
    parentOptionsLoading.value = false
  }
}
watch(
  () => schema.productType,
  (newVal) => {
    if (newVal === 'parent') {
      schema.parentPath = ''
    } else if (newVal === 'child' && !schema.parentPath) {
      // When changing to child, preselect the first parent if none is selected
      if (parentOptions.value.length > 0) {
        schema.parentPath = parentOptions.value[0].productId || parentOptions.value[0].path
        console.log('Auto-selected first parent:', schema.parentPath)
      }
    }
  }
)

// Form validation
const isFormValid = computed(() => {
  // Product name is required
  if (!schema.productName || schema.productName.trim() === '') {
    return false
  }

  // Product name must not already exist
  if (productNameError.value) {
    return false
  }

  // Friendly name is required
  if (
    !schema.commonData.productFriendlyName ||
    schema.commonData.productFriendlyName.trim() === ''
  ) {
    return false
  }

  // If product is a child, parent path is required
  if (schema.productType === 'child' && (!schema.parentPath || schema.parentPath.trim() === '')) {
    return false
  }

  // Dashboard URL validation (if provided, must be valid)
  if (dashboardUrl.value && errorMessages.value.length > 0) {
    return false
  }

  return true
})

const loadExistingProducts = async () => {
  try {
    const response = await fetchAllProductsFromIndex()
    console.log('fetchAllProductsFromIndex response:', response)

    // The response structure is { products: [...], totalProducts, totalActive, etc }
    if (response && response.products) {
      existingProducts.value = response.products
      console.log(
        'Loaded existing products:',
        existingProducts.value.length,
        existingProducts.value
      )
    } else {
      console.warn('No products found in response:', response)
    }
  } catch (error) {
    console.error('Error loading existing products:', error)
    showSnack('Failed to load existing products', 'error')
  }
}

onMounted(async () => {
  await Promise.all([getParentAccounts(), loadExistingProducts(), loadParentOptionsFromIndex()])
})
</script>

<template>
  <v-container class="mb-16">
    <div class="d-flex flex-wrap justify-between align-center" style="gap: 10px">
      <div class="d-flex align-center" style="gap: 8px">
        <v-btn
          variant="text"
          size="small"
          prepend-icon="mdi-arrow-left"
          class="text-grey-darken-2"
          @click="$router.push('/products')"
        >
          Back
        </v-btn>
        <h1 style="margin: 0">Add New Product</h1>
      </div>
    </div>

    <p class="my-6 text-body-2 text-grey-darken-1">
      Use this form to create and customize a new product template.
    </p>

    <v-card class="mb-6">
      <v-card-text>
        <v-text-field
          v-model="schema.productName"
          label="Product Name *"
          outlined
          data-testid="product-name-input"
          :rules="[(v) => !!v || 'Product name is required']"
          :error-messages="productNameError"
        ></v-text-field>
        <v-text-field
          v-model="schema.commonData.productFriendlyName"
          label="Friendly Name *"
          outlined
          :rules="[(v) => !!v || 'Friendly name is required']"
        ></v-text-field>
        <v-row>
          <v-checkbox
            v-model="schema.productType"
            true-value="child"
            false-value="parent"
            label="Is Child?"
          ></v-checkbox>
        </v-row>
        <v-row class="mb-2" v-if="schema.productType === 'child'">
          <v-col>
            <label for="">Parent Name *</label>
            <v-autocomplete
              v-model="schema.parentPath"
              :items="parentOptions"
              :loading="parentOptionsLoading"
              item-title="displayName"
              item-value="productId"
              label="Select parent product"
              density="comfortable"
              variant="outlined"
              hide-details="auto"
              :hint="'Select any product to be the parent (supports multi-level hierarchy)'"
              persistent-hint
              :rules="[(v) => !!v || 'Parent is required for child products']"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-title class="d-flex align-center">
        Product Fields
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="addField">Add Field</v-btn>
      </v-card-title>
      <v-card-text>
        <v-expansion-panels>
          <v-expansion-panel v-for="(field, index) in fields" :key="index" class="pa-4">
            <v-expansion-panel-title>
              {{ field.key || 'New Field' }} ({{ field.type }})
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="field.key"
                    label="Field Name"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="field.type"
                    :items="['string', 'number', 'object', 'array', 'boolean']"
                    label="Field Type"
                    outlined
                    dense
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-checkbox
                    v-model="field.required"
                    label="Required"
                    dense
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" md="1">
                  <v-btn icon color="error" @click="removeField(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <div v-if="field.type === 'array'" class="mt-4">
                <v-select
                  v-model="field.arrayType"
                  :items="['string', 'number', 'object']"
                  label="Array Item Type"
                  outlined
                  dense
                ></v-select>
                <div v-if="field.arrayType === 'object'" class="mt-4">
                  <div class="d-flex align-center mb-2">
                    <h4>Array Object Properties</h4>
                    <v-btn
                      variant="text"
                      color="primary"
                      @click="addArrayObjectProperty(field)"
                      class="ml-2"
                    >
                      Add Property
                    </v-btn>
                  </div>
                  <v-card
                    v-for="(prop, propIndex) in field.arrayObjectProperties"
                    :key="propIndex"
                    outlined
                    class="mb-2 pa-2"
                  >
                    <v-row align="center">
                      <v-col cols="12" md="5">
                        <v-text-field
                          v-model="prop.key"
                          label="Property Name"
                          outlined
                          dense
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="prop.type"
                          :items="['string', 'number', 'boolean']"
                          label="Type"
                          outlined
                          dense
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="2">
                        <v-checkbox
                          v-model="prop.required"
                          label="Required"
                          dense
                          :disabled="!field.required"
                        ></v-checkbox>
                      </v-col>
                      <v-col cols="12" md="1">
                        <v-btn
                          icon
                          small
                          color="error"
                          @click="removeArrayObjectProperty(field, propIndex)"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card>
                </div>
              </div>
              <div v-if="field.type === 'object'" class="mt-4">
                <div class="d-flex align-center mb-2">
                  <h4>Object Properties</h4>
                  <v-btn
                    color="primary"
                    @click="addObjectProperty(field)"
                    class="ml-2"
                  >
                    Add Property
                  </v-btn>
                </div>
                <v-card
                  v-for="(prop, propIndex) in field.objectProperties"
                  :key="propIndex"
                  outlined
                  class="mb-2 pa-2"
                >
                  <v-row align="center">
                    <v-col cols="12" md="5">
                      <v-text-field
                        v-model="prop.key"
                        label="Property Name"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="prop.type"
                        :items="['string', 'number', 'boolean']"
                        label="Type"
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-checkbox
                        v-model="prop.required"
                        label="Required"
                        dense
                        :disabled="!field.required"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="1">
                      <v-btn
                        icon
                        small
                        color="error"
                        @click="removeObjectProperty(field, propIndex)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

    <v-card class="mt-6">
      <v-card-title>Template Preview</v-card-title>
      <v-card-text>
        <pre class="schema-preview">{{ JSON.stringify(schema, null, 2) }}</pre>
      </v-card-text>
    </v-card>

    <v-card class="mt-5">
      <v-text-field
        v-model="dashboardUrl"
        label="Dashboard Url"
        outlined
        dense
        :error-messages="errorMessages"
      ></v-text-field>
    </v-card>
    <v-card class="mt-5 pl-4 pt-3 pb-3">
      <p>
        Toggle this switch to control product visibility. When disabled, the product will still be
        added to the account but hidden from customers.
      </p>
      <v-switch
        v-model="showToCustomers"
        :label="showToCustomers ? 'Visible to Customers' : 'Hidden from Customers'"
        :color="showToCustomers ? 'success' : 'error'"
        inset
        hide-details
        class="text-h6"
        density="comfortable"
      />
    </v-card>

    <v-fade-transition>
      <div class="floating-footer">
        <v-container class="py-2 d-flex justify-end ga-2">
          <v-btn
            variant="tonal"
            color="grey"
            data-testid="cancel-btn"
            @click="router.push('/products')"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            data-testid="create-product-btn"
            @click="submitSchema"
            :disabled="loading || !isFormValid"
          >
            {{ loading ? 'Creating Product' : 'Create Product' }}
          </v-btn>
        </v-container>
      </div>
    </v-fade-transition>
    <v-snackbar v-model="snackbar" :color="snackbarColor" location="bottom center" timeout="2000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<style scoped>
.schema-builder {
  max-width: 1200px;
  margin: 0 auto;
}
.schema-preview {
  background: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
  white-space: pre-wrap;
  font-family: monospace;
}
.floating-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
  z-index: 10;
}
</style>
